import React from 'react';

const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const CollectionPrint = React.forwardRef(({ monthRows, remarkNames, grandTotal, fromDate, toDate, propertyName }, ref) => {
    return (
        <div ref={ref} className="collection-print">
            <style>
                {`
                    @page {
                        size: A3;
                        margin: 7mm;
                    }
                    .collection-print {
                        display: none;
                        font-family: Arial, Helvetica, sans-serif;
                        color: #1a1a1a;
                        background: #fff;
                    }
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        .collection-print,
                        .collection-print * {
                            visibility: visible;
                        }
                        .collection-print {
                            display: block !important;
                            width: 100%;
                            box-sizing: border-box;
                        }
                        .report-top {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            padding-bottom: 8px;
                            margin-bottom: 8px;
                        }
                        .report-left {
                            text-align: left;
                        }
                        .report-center {
                            text-align: center;
                        }
                        .company-name {
                            font-size: 20px;
                            font-weight: 700;
                            line-height: 1.2;
                            margin-bottom: 3px;
                        }
                        .report-title {
                            font-size: 16px;
                            font-weight: 700;
                            letter-spacing: 1px;
                            text-transform: uppercase;
                            color: #333;
                        }
                        .report-right {
                            text-align: right;
                            padding-left: 15px;
                            min-width: 170px;
                        }
                        .period-label {
                            font-size: 8px;
                            color: #777;
                            text-transform: uppercase;
                            letter-spacing: .5px;
                            margin-bottom: 2px;
                        }
                        .period-value {
                            font-size: 10px;
                            font-weight: 700;
                        }
                        .print-table {
                            width: 100%;
                            border-collapse: collapse;
                            table-layout: fixed;
                        }
                        .print-table th,
                        .print-table td {
                            border: 1px solid #aaa;
                            padding: 3px 4px;
                            font-size: 8px;
                            line-height: 1.15;
                        }
                        .print-table thead {
                            display: table-header-group;
                        }
                        .print-table th {
                            background: #ededed !important;
                            color: #111 !important;
                            font-weight: 700;
                            text-align: center;
                            vertical-align: middle;
                            white-space: normal;
                            overflow-wrap: anywhere;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .print-table th:first-child {
                            width: 7%;
                        }
                        .print-table th:last-child {
                            width: 9%;
                            border-left: 1px solid #333;
                        }
                        .print-table tbody tr {
                            page-break-inside: avoid;
                        }
                        .print-table tbody tr:nth-child(even) td {
                            background: #fafafa !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .month-cell {
                            font-weight: 700;
                            text-align: left;
                            white-space: nowrap;
                        }
                        .number-cell {
                            text-align: right;
                            white-space: nowrap;
                            font-variant-numeric: tabular-nums;
                        }
                        .total-cell {
                            font-weight: 700;
                            border-left: 1px solid #333 !important;
                        }
                        .grand-total td {
                            background: #dedede !important;
                            font-weight: 700 !important;                           
                            padding-top: 5px !important;
                            padding-bottom: 5px !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .grand-total .month-cell {
                            text-transform: uppercase;
                            letter-spacing: .4px;
                        }
                        .summary-box {
                            display: flex;
                            justify-content: flex-end;
                            margin-top: 7px;
                        }
                        .summary-total {
                            display: flex;
                            align-items: center;
                            border: 1px solid #333;
                            min-width: 240px;
                        }
                        .summary-label {
                            padding: 5px 10px;
                            font-size: 9px;
                            font-weight: 700;
                            text-transform: uppercase;
                            background: #ededed !important;
                            border-right: 1px solid #333;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .summary-value {
                            padding: 5px 12px;
                            font-size: 11px;
                            font-weight: 700;
                            text-align: right;
                            flex: 1;
                        }
                        .print-footer {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 7px;
                            padding-top: 4px;
                            border-top: 1px solid #aaa;
                            font-size: 7.5px;
                            color: #666;
                        }
                    }
                `}
            </style>
            <div className="report-top">
                <div className="report-left">
                    <div className="report-title">Collection Summary</div>
                </div>
                <div className="report-center">
                    <div className="company-name">{propertyName || 'Unknown Property'}</div>
                </div>
                <div className="report-right">
                    <div className="period-label">Report Period</div>
                    <div className="period-value">
                        {fromDate?.format('DD/MM/YYYY')} - {toDate?.format('DD/MM/YYYY')}
                    </div>
                </div>
            </div>
            <table className="print-table">
                <thead>
                    <tr>
                        <th>Month</th>
                        {remarkNames.map((name) => (
                            <th key={name}>{name}</th>
                        ))}
                        <th>Total Amount ₹</th>
                    </tr>
                </thead>
                <tbody>
                    {monthRows.map((monthRow) => (
                        <tr key={monthRow.monthKey}>
                            <td className="month-cell">{monthRow.monthLabel}</td>
                            {remarkNames.map((name) => {
                                const amount = monthRow.amountByRemark?.[name] || 0;
                                return (
                                    <td key={name} className="number-cell">
                                        {amount ? formatAmount(amount) : '-'}
                                    </td>
                                );
                            })}
                            <td className="number-cell total-cell">
                                {monthRow.total ? formatAmount(monthRow.total) : '-'}
                            </td>
                        </tr>
                    ))}
                    <tr className="grand-total">
                        <td className="month-cell">Grand Total</td>
                        {remarkNames.map((name) => (
                            <td key={name} className="number-cell">
                                {formatAmount(grandTotal.amountByRemark?.[name])}
                            </td>
                        ))}
                        <td className="number-cell total-cell">
                            {formatAmount(grandTotal.total)}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="print-footer">
                <span>Collection Summary Report</span>
                <span>Generated from system records</span>
                <span>Printed on: {new Date().toLocaleString('en-IN')}</span>
            </div>
        </div>
    );
});

export default CollectionPrint;
